import React from 'react';
import { Column, Row } from 'Core/components/Layout';
import Dropdown from 'Core/components/Dropdown/Dropdown';
import Icon from 'Core/components/Icon/Icon';

import { formatCurrency } from 'Core/utils/numberFormat';
import { generateURL } from 'Core/utils/utils';

import { useGetTransactionHistory } from 'Core/hooks/platform/useGetTransactionHistory';
import { useGetPaymentInfo } from 'Core/hooks/platform/useGetPaymentInfo';
import { getEstimatedMonthlyPayment } from 'Modules/fortiva/utils/calcMonthlyPayment';
import constants from 'Modules/fortiva/config/constants';

import cacheKeys from 'Core/hooks/queries/cacheKeys';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-location';
import { TextButton } from 'Core/components/Button';
import Tooltip from 'Core/components/Tooltip';
import { useTranslation } from 'react-i18next';

// Temporarily disabled: down payment is collected after the application is
// submitted, so the inline Collect Payment button is hidden for now.
const SHOW_COLLECT_PAYMENT_BUTTON = false;

export default function FinancingSummary({
  routes,
  watch,
  application,
  setRefetchInterval,
  selectedOffer,
  onPrepareCollectPayment,
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isCollectPaymentLoading, setIsCollectPaymentLoading] = React.useState(false);
  const { data: transactions } = useGetTransactionHistory();
  const { data: paymentInfo } = useGetPaymentInfo() || {};

  const [receiptList, setReceiptList] = React.useState([]);

  const {
    next_action,
    payload: {
      already_paid_amount = 0,
      approval = 0,
      offers,
      selected_offer_id,
      down_payment_status,
    } = {},
  } = application ?? {};
  const isDownPaymentPaid = down_payment_status === 'PAID';
  const priceInfo = application?.payload?.offer_term?.price;

  const amountToPay = Number(paymentInfo?.amount_to_pay) || 0;
  const downPayment = Number(watch('downPayment')) || 0;
  const selectedTermId = selected_offer_id || selectedOffer?.id;

  const financed = priceInfo?.financed ?? Math.max(amountToPay - downPayment, 0);

  const backendMonthly = Number(priceInfo?.monthly_payment);
  // min_payment_pct always wins when present; backend value and amortization are fallbacks.
  const monthlyPayment = getEstimatedMonthlyPayment({
    offers,
    selectedOfferId: selectedTermId,
    financed,
    backendMonthly,
  });

  const availableCredit = Number(approval) || 0;
  const additionalCollectedAmount = downPayment - already_paid_amount;
  const handleUnload = (e) => {
    const message = '';

    (e || window.event).returnValue = message;
    return message;
  };
  React.useEffect(() => {
    if (transactions && transactions.length > 0) {
      setReceiptList(
        transactions.map((item, idx, arr) => {
          const receiptNum = arr.length === 1 ? '' : idx + 1;
          return { label: 'Receipt ' + receiptNum, key: item?.payment_id, url: item?.receipt?.url };
        })
      );
    }
  }, [transactions]);

  React.useEffect(() => {
    if (isDownPaymentPaid) {
      setIsCollectPaymentLoading(false);
    }
    if (next_action === 'DONE' || next_action === 'PENDING_DOWN_PAYMENT') {
      navigate({ to: routes.financing, search: true });
    }
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [isDownPaymentPaid, next_action, navigate, routes.financing]);

  const onCollectPayment = async () => {
    setIsCollectPaymentLoading(true);

    try {
      await onPrepareCollectPayment();
    } catch {
      setIsCollectPaymentLoading(false);
      return;
    }

    // Buffer before opening the payment URL so the backend has settled and we don't read stale data.
    await new Promise((resolve) => {
      setTimeout(resolve, constants.UPDATE_OFFER_BUFFER_DELAY_MS);
    });

    const url = generateURL({ url: routes.mixed });
    setRefetchInterval(5000);
    queryClient.invalidateQueries(cacheKeys.financingNextStep);

    setTimeout(() => setIsCollectPaymentLoading(false), 5 * 60 * 1000);
    window.open(url, '_blank')?.focus();
  };

  return (
    <>
      <h3 className="text-center mb-4">{t('common.summary')}</h3>

      <Row className="mb-1 mx-0">
        <Column className="!flex-none lg:!flex-col-3 px-2 "></Column>
        <Column
          className="!flex-col-7 lg:!flex-col-4 px-2 text-left"
          data-testid="label-available-credit"
        >
          {t('common.availableCredit')}
        </Column>
        <Column className="!flex-col-2 text-right px-2 " data-testid="value-available-credit">
          {availableCredit > 0 ? formatCurrency(availableCredit) : '-'}
        </Column>
      </Row>
      {isDownPaymentPaid && additionalCollectedAmount > 0 && (
        <Row className="items-center mx-0 flex-nowrap">
          <Column className="!flex-none lg:!flex-col-3 px-2 "></Column>
          <Column
            className="!flex-col-7 lg:!flex-col-4 px-2 text-left"
            data-testid="label-additional-collected-amount"
          >
            {t('financing.additionalCollectedAmount')}
          </Column>
          <Column
            className="!flex-col-2 flex justify-end items-center px-2"
            data-testid="value-additional-collected-amount"
          >
            <Icon type="success" />
            {formatCurrency(additionalCollectedAmount)}
          </Column>
          <Column className="!flex-col-3 text-left">
            <>
              {transactions &&
                transactions.length === 1 &&
                receiptList.map(({ url, key }) => {
                  return (
                    <a
                      className="bg-transparent text-primary hover:bg-gray-100 p-2 rounded text-sm"
                      target="_blank"
                      rel="noreferrer"
                      data-testid="receipt-item"
                      href={url}
                      key={key}
                    >
                      {t('common.viewReceipt')}
                    </a>
                  );
                })}

              {receiptList && receiptList.length > 1 && (
                <Dropdown label={t('common.viewReceipt')} list={receiptList}></Dropdown>
              )}
            </>
          </Column>
        </Row>
      )}
      {!isDownPaymentPaid && additionalCollectedAmount > 0 && (
        <Row className="items-center mx-0  flex-nowrap">
          <Column className="!flex-none lg:!flex-col-3 px-2 "></Column>
          <Column
            className="!flex-col-7 lg:!flex-col-4 px-2 text-left font-semibold"
            data-testid="label-amount-to-collect"
          >
            {t('fortiva.finalize.summary.amountToBeCollected')}
          </Column>
          <Column className="!flex-col-2 text-right flex justify-end items-center px-2">
            <span className="font-semibold" data-testid="value-amount-to-collect">
              {formatCurrency(additionalCollectedAmount)}
            </span>
          </Column>
          <Column className="!flex-col-3 text-left">
            {SHOW_COLLECT_PAYMENT_BUTTON ? (
              <TextButton
                disabled={isCollectPaymentLoading}
                onClick={onCollectPayment}
                data-testid="btn-collect-payment"
              >
                <div className="flex">
                  {isCollectPaymentLoading && (
                    <span className="inline-block mr-3">
                      <Icon type="spinner"></Icon>
                    </span>
                  )}
                  <span className="!hidden lg:!block">{t('financing.collectPayment')}</span>
                  <span className="!block lg:!hidden">{'Collect'}</span>
                </div>
              </TextButton>
            ) : (
              // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- hidden admin test shortcut, intentionally not exposed as an interactive control
              <span
                data-testid="down-payment-info-tooltip"
                onClick={(e) => {
                  // Admin test shortcut: shift-click launches the collect-payment flow
                  // while the regular button is disabled.
                  if (e.shiftKey && !isCollectPaymentLoading) {
                    onCollectPayment();
                  }
                }}
              >
                <Tooltip
                  content={t('fortiva.finalize.summary.downPaymentInfo', {
                    defaultValue: 'Down payment can be processed after submitting the application',
                  })}
                />
              </span>
            )}
          </Column>
        </Row>
      )}

      <Row className="mb-4 mx-0">
        <Column className="!flex-none lg:!flex-col-3 px-2 "></Column>
        <Column
          className="!flex-col-7 lg:!flex-col-4 px-2 text-left"
          data-testid="label-total-down-payment"
        >
          {t('financing.totalDownPayment')}
        </Column>
        <Column className="!flex-col-2 text-right px-2" data-testid="value-total-down-payment">
          {formatCurrency(watch('downPayment') || 0)}
        </Column>
      </Row>
      <Row className="mb-1 mx-0">
        <Column className="!flex-none lg:!flex-col-3 px-2 "></Column>
        <Column
          className="!flex-col-7 lg:!flex-col-4 px-2 text-left"
          data-testid="label-funding-by-allegro"
        >
          {t('fortiva.fundingByFortiva')}
        </Column>
        <Column className="!flex-col-2 text-right px-2" data-testid="value-funding-by-allegro">
          {Number.isFinite(financed)
            ? formatCurrency(financed, { withDecimals: financed % 1 > 0 })
            : '-'}
        </Column>
      </Row>

      <Row className="mb-1 mx-0">
        <Column className="!flex-none lg:!flex-col-3 px-2"></Column>
        <Column
          className="!flex-col-7 lg:!flex-col-4 px-2 text-left"
          data-testid="label-monthly-payment"
        >
          Estimated Initial Monthly Payment<span aria-hidden="true">*</span>
        </Column>
        <Column className="!flex-col-2 text-right px-2 " data-testid="value-monthly-payment">
          {Number.isFinite(monthlyPayment) && monthlyPayment > 0
            ? formatCurrency(monthlyPayment, { withDecimals: true })
            : '-'}
        </Column>
      </Row>
    </>
  );
}
